import { ReactElement, useEffect, useState } from 'react';
import cx from 'clsx';
import { useField } from 'react-final-form';
import { Box, DatePicker, FieldErrorTooltip, Input, useSafeId } from '@ff-it/ui';
import { formatDate, humanizeName } from 'utilities';
import { FilterChip } from './FilterChip';
import { dateCalendar, dateInputSeparator } from './dateChip.css';
import { INPUT_FORMAT, fmtInput, parseInput, toDate, toISO } from './dateChip';

export function DateFilterChip({ name, label }: { name: string; label?: string }): ReactElement {
  const errorId = useSafeId();
  const { input } = useField(name, { subscription: { value: true } });
  const value = input.value as string | undefined;
  const active = value !== undefined && value !== null && value !== '';

  const selected = toDate(value);
  const [text, setText] = useState<string>(fmtInput(selected));
  const [showError, setShowError] = useState(false);
  useEffect(() => setText(fmtInput(toDate(value))), [value]);

  const parsed = parseInput(text);
  const invalid = !!text.trim() && !parsed;
  const commit = (d: Date | null): void => input.onChange(toISO(d));
  const commitText = (): void => {
    if (parsed || !text.trim()) {
      commit(parsed);
    }
  };

  return (
    <FilterChip
      label={label ?? humanizeName(name)}
      active={active}
      valueNode={active ? formatDate(value as string) : null}
      onClear={() => input.onChange(undefined)}
      onClose={() => {
        commitText();
        setText(fmtInput(toDate(input.value)));
        setShowError(false);
      }}
      width={null}
      flush
    >
      {(close) => (
        <Box>
          <div className={cx('position-relative px-2 pt-2 pb-2', dateInputSeparator)}>
            <Input
              size="sm"
              valid={showError && invalid ? false : undefined}
              aria-describedby={showError && invalid ? errorId : undefined}
              placeholder={INPUT_FORMAT}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setShowError(false);
              }}
              onBlur={() => {
                commitText();
                setShowError(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitText();
                  close();
                }
              }}
            />
            {showError && invalid && <FieldErrorTooltip id={errorId}>Invalid date</FieldErrorTooltip>}
          </div>
          <Box className={dateCalendar}>
            <DatePicker inline selected={selected} onChange={(d: Date | null) => commit(d)} />
          </Box>
        </Box>
      )}
    </FilterChip>
  );
}
